import { Component, inject, ViewChild, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ApiService } from './app/services/api/api.service';
import { DesktopSsoService } from './app/core/tauri/desktop-sso.service';
import { LayoutService } from './app/layout/service/layout.service';
import { distinctUntilChanged, filter, map, mergeMap } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { AppConfigurator } from './app/layout/component/app.configurator';
import { ContextService } from './app/services/context/context.service';
import { ContextStoreService } from './app/services/context/context-store.service';
import { PrimeNG } from 'primeng/config';
import { PermissionsService } from './app/services/permissions/permissions.service';
import { ToastModule } from 'primeng/toast';
import { VideoTutorialModalComponent } from './app/shared/video-tutorial-modal/video-tutorial-modal.component';
import { DomainService } from './app/services/domain/domain.service';
import { SelectionButtonComponent } from './app/shared/selection-button/selection-button.component';
import { TextSelectionService } from './app/services/text-selection/text-selection.service';
import { CommonModule } from '@angular/common';
import { ChatRoomComponent } from './app/shared/chat-room/chat-room.component';
import { OffersComponent } from './app/shared/offers/offers.component';
import { PaymentReminderService } from './app/services/payment-reminder/payment-reminder.service';
import { CreditReminderService } from './app/services/credit-reminder/credit-reminder.service';
import { DownloadIndicatorComponent } from './app/shared/download-indicator/download-indicator.component';
import { LightAccountService } from './app/services/light-account/light-account.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule, ToastModule, ChatRoomComponent, CommonModule, VideoTutorialModalComponent, SelectionButtonComponent, OffersComponent, DownloadIndicatorComponent],
  providers: [AppConfigurator],
  template: `
  <div [ngClass]="environmentType">
    <div class="main-content-wrapper">
      <p-toast key="tst"></p-toast>
      <router-outlet></router-outlet>
      <app-video-tutorial-modal
        #videoModal
        videoUrl="https://www.youtube.com/watch?v=SVZneyVxh4o"
        [title]="'Prise en main de ' + domainName">
      </app-video-tutorial-modal>

      <app-selection-button *ngIf="!isPublicPage"></app-selection-button>
      <app-chat-room *ngIf="!isPublicPage"></app-chat-room>
      <app-download-indicator *ngIf="!isPublicPage"></app-download-indicator>
    </div>

    <!-- Les modales restent en dehors du wrapper -->
    <app-offers
      *ngIf="showOffersModal && !isPublicPage"
      [context]="contextStore.currentContext"
      [is_orga_only]="true"
      [isPaymentRequired]="true"
      (closeModal)="showOffersModal = false">
    </app-offers>

    <app-offers
      *ngIf="showCreditsModal && !isPublicPage"
      [context]="contextStore.currentContext"
      [isCreditsRequired]="true"
      (closeModal)="showCreditsModal = false">
    </app-offers>
  </div>
  `
})
export class AppComponent implements OnInit {
  layoutService: LayoutService = inject(LayoutService);
  AppConfigurator: AppConfigurator = inject(AppConfigurator);
  domainName: string;
  private getMeInterval: any;
  isPublicPage = false;
  showOffersModal: boolean = false;
  showCreditsModal: boolean = false;

  constructor(
    private contextService: ContextService,
    private primengConfig: PrimeNG,
    public contextStore: ContextStoreService,
    private apiService: ApiService,
    private titleService: Title,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private permissionsService: PermissionsService,
    private domainService: DomainService,
    private textSelectionService: TextSelectionService,
    private cdr: ChangeDetectorRef,
    private paymentReminderService: PaymentReminderService,
    private creditReminderService: CreditReminderService,
    private desktopSso: DesktopSsoService,
    private lightAccount: LightAccountService
  ) {
    this.autoChangePageTitle();
    this.domainName = this.domainService.getDomain();
  }

  @ViewChild('videoModal') videoModal!: VideoTutorialModalComponent;

  ngOnInit() {
    this.detectEnvironment();
    this.permissionsService.initUserFromStorage();

    // En desktop (Tauri) : branche l'écouteur de deep-link pour le retour SSO.
    // No-op en web classique.
    this.desktopSso.init();

    // S'abonner au service de rappel de paiement
    this.paymentReminderService.shouldShowOffers$.subscribe((shouldShow) => {
      this.showOffersModal = shouldShow;
    });

    // S'abonner au service de rappel de crédits
    this.creditReminderService.shouldShowCredits$.subscribe((shouldShow) => {
      this.showCreditsModal = shouldShow;
    });

    if (localStorage.getItem('access_token')) {
      this.apiService.getMe().subscribe((res) => {
        const user = res;
        const context = this.contextStore.currentContext;
        this.permissionsService.setCurrentUser(user);

        // Initialisation uniquement si pas de contexte et pas sur une route de switch de base
        // BaseSwitchComponent gère son propre contexte
        if (!context && user?.companies?.length && !this.router.url.startsWith('/bases/')) {
          const activeBaseFromStorage = localStorage.getItem('activeBase');

          if (activeBaseFromStorage) {
            // RESTAUER depuis localStorage (refresh ou retour sur l'app)
            const base = JSON.parse(activeBaseFromStorage);

            // Chercher dans user.companies pour données fraîches
            const baseFromUser = user.companies.find((b: any) => b.id === base.id);
            const baseToUse = baseFromUser || base;

            this.contextService.setActiveBase(baseToUse, user);
          } else {
            // Première initialisation (pas de localStorage)
            const defaultBase = user.companies[0];
            this.contextService.setActiveBase(defaultBase, user);
          }
        }
        // Si context existe déjà, ne rien faire
      });

    }

    // Switch de base : rafraîchir immédiatement les soldes (pill NrTok) au
    // lieu d'attendre le prochain tick du polling 20 s. Le distinctUntilChanged
    // sur l'id de la base évite toute boucle avec updateActiveBaseData, qui
    // réémet le contexte (nouvel objet) sans en changer la base.
    this.contextStore.getContext()
      .pipe(
        filter((context) => !!context?.base),
        distinctUntilChanged((a, b) => a?.base?.id === b?.base?.id),
      )
      .subscribe(() => {
        if (localStorage.getItem('access_token')) {
          this.getAndUpdateUserData();
        }
      });

    this.getMeInterval = setInterval(() => {
      if (localStorage.getItem('access_token')) {
        this.getAndUpdateUserData();
      }
    }, 20000);

    this.setUpTranslation();
    this.setCorrectTheme();

    this.apiService.currentUserChange.subscribe((user: any) => {
      if (user && !user.is_light_account && user.login_count == 1 && localStorage.getItem('videoTutorialSeen') !== 'true') {
        this.videoModal.openModal()

        // Redirection automatique selon le flag utilisateur
        if (user.flag === 'BOTADS' || user.flag === 'botads_convert' || user.flag === 'chatbothenrri_convert') {
          // Délai plus long pour écraser la redirection vers /home depuis login.ts
          setTimeout(() => {
            this.router.navigate(['/agents/html'], { replaceUrl: true });
          }, 600);
        }
      }
    })

    document.addEventListener('mouseup', () => {
      setTimeout(() => this.textSelectionService.updateSelection(), 10);
    });

    document.addEventListener('keyup', (e) => {
      // Only update when selection keys are potentially used
      if (e.key === 'Shift' || e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' || e.key === 'ArrowUp' ||
        e.key === 'ArrowDown') {
        setTimeout(() => this.textSelectionService.updateSelection(), 10);
      }
    });


    this.updateIsPublicPage(this.router.url);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateIsPublicPage(event.urlAfterRedirects);
    });
  }

  private updateIsPublicPage(url: string) {
    const publicPaths = [
      '/login',
      '/register',
      '/register-success',
      '/forgot-password',
      '/reset-password',
      '/verify',
      '/payment-setup',
      '/share',
      '/base',
      '/aide',
      '/desktop-sso',
      '/agents',
      '/scrap',
      '/inscription',
      '/marketplace'
    ];

    // si c'est la racine exacte
    const isRoot = url === '/';
    this.isPublicPage = isRoot || publicPaths.some(path => url.startsWith(path));
    this.cdr.detectChanges();
  }


  getAndUpdateUserData() {
    // Compte léger : pas de base active ni de pill NrTok du BO (la
    // marketplace rafraîchit son propre solde).
    if (this.lightAccount.isLight()) return;
    let context = this.contextStore.currentContext;
    this.apiService
      .updateNumCredits(context?.base.id)
      .subscribe((user: any) => this.refreshActiveBaseBalances(user));
  }

  /**
   * Rafraîchit les soldes NrTok de la base active dans le contexte (pill de la
   * topbar). Le pool étant porté par l'organisation, si la base active n'est
   * plus dans user.companies (ex. base d'une orga quittée), on lit le solde
   * effectif sur n'importe quelle base de la même orga. Sans ça, le contexte
   * garde un snapshot pris au login et affiche 0 NrToks pour une organisation
   * créditée après coup, alors que /admin/management (fetch frais) est correct.
   */
  private refreshActiveBaseBalances(user: any) {
    const context = this.contextStore.currentContext;
    if (!user || !context?.base) return;

    const baseId = context.base.id;
    const orgId = context.base.organisation_id;
    const fresh =
      user.companies?.find((c: any) => c.id === baseId) ??
      (orgId
        ? user.companies?.find((c: any) => c.organisation_id === orgId)
        : null);
    if (!fresh) return;

    const balanceFields = [
      'credits',
      'credits_free',
      'credits_effective',
      'credits_free_effective',
      'is_nolimit_credits',
      'is_nolimit_effective',
    ];
    const changed = balanceFields.some(
      (f) => !Object.is(context.base[f], fresh[f]),
    );
    if (!changed) return;

    this.contextService.updateActiveBaseData({
      ...context.base,
      credits: fresh.credits,
      credits_free: fresh.credits_free,
      credits_effective: fresh.credits_effective,
      credits_free_effective: fresh.credits_free_effective,
      is_nolimit_credits: fresh.is_nolimit_credits,
      is_nolimit_effective: fresh.is_nolimit_effective,
    });
  }


  ngOnDestroy() {
    // Nettoyer l'intervalle lorsque le composant est détruit
    if (this.getMeInterval) {
      clearInterval(this.getMeInterval);
    }
  }

  setCorrectTheme() {
    if (localStorage.getItem('darkTheme') == undefined) { localStorage.setItem('darkTheme', 'false'); }
    const darkTheme = localStorage.getItem('darkTheme') ?? 'false';
    this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: darkTheme === 'true' }));


    if (localStorage.getItem('themePreset') == undefined) { localStorage.setItem('themePreset', 'Aura'); }
    const themePreset = localStorage.getItem('themePreset') ?? 'Aura';
    this.AppConfigurator.onPresetChange(themePreset);

    if (localStorage.getItem('menuMode') == undefined) { localStorage.setItem('menuMode', 'static'); }
    const menuMode = localStorage.getItem('menuMode') ?? 'static';
    this.AppConfigurator.onMenuModeChange(menuMode);

    if (localStorage.getItem('themeColor') == undefined) { localStorage.setItem('themeColor', 'blue'); }
    const colorTheme = localStorage.getItem('themeColor') ?? 'blue';
    this.layoutService.layoutConfig.update((state) => ({ ...state, primary: colorTheme }));

    if (localStorage.getItem('themeSurface') == undefined) { localStorage.setItem('themeSurface', 'null'); }
    const colorSurface = localStorage.getItem('themeSurface') ?? 'null';
    const surface = this.AppConfigurator.surfaces.find((surface) => surface.name === colorSurface);
    this.layoutService.layoutConfig.update((state) => ({ ...state, surface: colorSurface }));

    this.AppConfigurator.applyTheme('primary', colorTheme);
    this.AppConfigurator.applyTheme('surface', surface);
  }


  autoChangePageTitle() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute.firstChild;
        while (route?.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      mergeMap(route => route?.data ?? [])
    ).subscribe(data => {
      if (data['title']) {
        this.titleService.setTitle(data['title']);
      }
    });
  }

  setUpTranslation() {

    const fr = {
      "startsWith": "Commence par",
      "contains": "Contient",
      "notContains": "Ne contient pas",
      "endsWith": "Se termine par",
      "equals": "Égal",
      "notEquals": "Pas égal",
      "noFilter": "Pas de filtre",
      "lt": "Moins que",
      "lte": "Moins que ou égal à",
      "gt": "Plus que",
      "gte": "Plus grand ou égal à",
      "is": "Est",
      "isNot": "N'est pas",
      "before": "Avant",
      "after": "Après",
      "dateIs": "La date est",
      "dateIsNot": "La date n'est pas",
      "dateBefore": "La date est avant",
      "dateAfter": "La date est après",
      "clear": "Effacer",
      "apply": "Appliquer",
      "matchAll": "Correspondre à tous",
      "matchAny": "Correspondre à n'importe quel",
      "addRule": "Ajouter une règle",
      "removeRule": "Supprimer la règle",
      "accept": "Oui",
      "reject": "Non",
      "choose": "Choisir",
      "upload": "Télécharger",
      "cancel": "Annuler",
      "dayNames": ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
      "dayNamesShort": ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
      "dayNamesMin": ["D", "L", "M", "M", "J", "V", "S"],
      "monthNames": ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      "monthNamesShort": ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"],
      "dateFormat": "dd/mm/yy",
      "firstDayOfWeek": 1,
      "today": "Aujourd'hui",
      "weekHeader": "Sem",
      "weak": "Faible",
      "medium": "Moyen",
      "strong": "Fort",
      "passwordPrompt": "Entrez un mot de passe",
      "emptyMessage": "Aucun résultat trouvé",
      "emptyFilterMessage": "Aucun résultat trouvé"
    };

    this.primengConfig.setTranslation(fr);
  }


  environmentType = 'prod'; // default

  private detectEnvironment() {
    const apiUrl = (window as any).__env?.apiUrl || '';
    
    if (apiUrl.includes('staging')) {
      this.environmentType = 'staging';
      document.documentElement.classList.add('env-staging');
    } else if (apiUrl.includes('dev')) {
      this.environmentType = 'dev';
      document.documentElement.classList.add('env-dev');
    }
  }
}
